import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './BookingsCalendar.css';
import axios from 'axios';
import InlineFeedback from '../components/InlineFeedback';

const resolveApiBaseUrl = () => {
  const configured = process.env.REACT_APP_API_BASE_URL;
  if (configured?.trim()) return configured.trim().replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const host = window.location.hostname;
    if (['localhost', '127.0.0.1', '::1', '[::1]'].includes(host)) return 'http://127.0.0.1:8000/api';
    return `http://${host}:8000/api`;
  }
  return 'http://127.0.0.1:8000/api';
};

const API_BASE = resolveApiBaseUrl();
const STATUS_COLORS = {
  pending: '#C58A18',
  confirmed: '#2D7D66',
  completed: '#6D3B7E',
  canceled: '#9CA3AF',
  no_show: '#B45309',
  failed: '#C2414B',
};
const STATUS_ORDER = ['pending', 'confirmed', 'completed', 'canceled', 'no_show', 'failed'];

const extractRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const dateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseBookingDate = (raw) => {
  const [year, month, day] = String(raw || '').split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

const personName = (profile, fallback) => {
  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ').trim();
  return fullName || profile?.user?.username || profile?.username || fallback;
};

const statusLabel = (status) => String(status || 'unknown').replaceAll('_', ' ');

const currencyFromCents = (cents, currency = 'usd') => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: String(currency || 'usd').toUpperCase(),
    }).format(Number(cents || 0) / 100);
  } catch {
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }
};

const roleContent = {
  client: {
    title: 'My Appointment Calendar',
    subtitle: 'See upcoming and previous appointments with your selected professionals.',
  },
  worker: {
    title: 'My Work Calendar',
    subtitle: 'See the client appointments assigned to your schedule.',
  },
  owner: {
    title: 'Company Booking Calendar',
    subtitle: 'See your appointments and bookings assigned across your company team.',
  },
  admin: {
    title: 'Platform Booking Calendar',
    subtitle: 'See all bookings across the platform for operational support.',
  },
};

const BookingsCalendar = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeMonth, setActiveMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadCalendar = useCallback(async () => {
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const headers = { Authorization: `Bearer ${token}` };
      const [profileResponse, bookingResponse] = await Promise.all([
        axios.get(`${API_BASE}/userprofiles/me/`, { headers }),
        axios.get(`${API_BASE}/bookings/`, { headers }),
      ]);
      setProfile(profileResponse.data);
      setBookings(extractRows(bookingResponse.data));
    } catch (requestError) {
      if (requestError?.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('userRole');
        window.dispatchEvent(new Event('auth-changed'));
        navigate('/login');
        return;
      }
      setError(requestError?.response?.data?.error || 'Unable to load the booking calendar.');
    } finally {
      setLoading(false);
    }
  }, [navigate, token]);

  useEffect(() => {
    loadCalendar();
  }, [loadCalendar]);

  const bookingsByDate = useMemo(() => bookings.reduce((grouped, booking) => {
    if (!booking.date) return grouped;
    if (!grouped[booking.date]) grouped[booking.date] = [];
    grouped[booking.date].push(booking);
    return grouped;
  }, {}), [bookings]);

  const selectedBookings = useMemo(() => (
    [...(bookingsByDate[dateKey(selectedDate)] || [])]
      .sort((left, right) => String(left.time || '').localeCompare(String(right.time || '')))
  ), [bookingsByDate, selectedDate]);

  const monthBookings = useMemo(() => bookings.filter((booking) => {
    const date = parseBookingDate(booking.date);
    return date.getFullYear() === activeMonth.getFullYear() && date.getMonth() === activeMonth.getMonth();
  }), [activeMonth, bookings]);

  const upcomingBookings = useMemo(() => {
    const today = dateKey(new Date());
    return bookings
      .filter((booking) => booking.date >= today && !['canceled', 'failed'].includes(booking.status?.current_status))
      .sort((left, right) => `${left.date}T${left.time || ''}`.localeCompare(`${right.date}T${right.time || ''}`))
      .slice(0, 5);
  }, [bookings]);

  const role = profile?.role || localStorage.getItem('userRole') || 'client';
  const content = roleContent[role] || roleContent.client;

  const bookingContext = (booking) => {
    const client = personName(booking.client, 'Client');
    const worker = personName(booking.worker, 'Unassigned professional');
    if (role === 'client') return `With ${worker}`;
    if (role === 'worker') return client;
    return `${client} with ${worker}`;
  };

  const serviceNames = (booking) => {
    const services = Array.isArray(booking.services) ? booking.services : [];
    return services.map((service) => service.name).filter(Boolean).join(', ') || booking.name || 'Beauty appointment';
  };

  const tileContent = ({ date, view }) => {
    if (view !== 'month') return null;
    const dayBookings = bookingsByDate[dateKey(date)] || [];
    if (!dayBookings.length) return null;
    const statuses = [...new Set(dayBookings.map((booking) => booking.status?.current_status || 'pending'))].slice(0, 3);
    return (
      <span className="booking-calendar__markers" aria-label={`${dayBookings.length} booking${dayBookings.length === 1 ? '' : 's'}`}>
        {statuses.map((status) => (
          <span
            key={status}
            className="booking-calendar__dot"
            style={{ backgroundColor: STATUS_COLORS[status] || '#6D3B7E' }}
          />
        ))}
        {dayBookings.length > 3 ? <span className="booking-calendar__count">{dayBookings.length}</span> : null}
      </span>
    );
  };

  const BookingCard = ({ booking, compact = false }) => {
    const status = booking.status?.current_status || 'pending';
    return (
      <article className={`booking-calendar__appointment ${compact ? 'booking-calendar__appointment--compact' : ''}`}>
        <div className="booking-calendar__appointment-heading">
          <div>
            <p className="booking-calendar__time">{String(booking.time || '').slice(0, 5) || 'Time pending'}</p>
            <h3>{serviceNames(booking)}</h3>
          </div>
          <span
            className="booking-calendar__status"
            style={{ '--status-color': STATUS_COLORS[status] || '#6D3B7E' }}
          >
            {statusLabel(status)}
          </span>
        </div>
        <p className="booking-calendar__person">{bookingContext(booking)}</p>
        {!compact ? (
          <div className="booking-calendar__meta">
            <span>{booking.duration_summary?.total_minutes || booking.duration_minutes || 0} min</span>
            <span>{currencyFromCents(booking.pricing_summary?.total_amount_cents ?? booking.total_amount_cents, booking.currency)}</span>
            {booking.payment_summary?.required ? <span>Payment: {statusLabel(booking.payment_summary.status)}</span> : null}
          </div>
        ) : <p className="booking-calendar__compact-date">{parseBookingDate(booking.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</p>}
      </article>
    );
  };

  return (
    <main className="booking-calendar-page">
      <div className="booking-calendar-page__inner">
        <header className="booking-calendar__hero">
          <div>
            <p className="booking-calendar__eyebrow">Bookings at a glance</p>
            <h1>{content.title}</h1>
            <p>{content.subtitle}</p>
          </div>
          <div className="booking-calendar__hero-actions">
            <button type="button" onClick={() => navigate('/myBookings')}>List view</button>
            {role === 'client' ? <button type="button" className="booking-calendar__primary" onClick={() => navigate('/booking')}>New booking</button> : null}
            <button type="button" onClick={() => navigate('/')}>Home</button>
          </div>
        </header>

        {error ? <InlineFeedback type="error" message={error} /> : null}
        {loading ? <InlineFeedback type="info" message="Loading your calendar..." /> : null}

        {!loading ? (
          <>
            <section className="booking-calendar__summary" aria-label="Calendar summary">
              <div><strong>{monthBookings.length}</strong><span>This month</span></div>
              <div><strong>{monthBookings.filter((booking) => booking.status?.current_status === 'confirmed').length}</strong><span>Confirmed</span></div>
              <div><strong>{upcomingBookings.length}</strong><span>Next appointments</span></div>
            </section>

            <section className="booking-calendar__layout">
              <div className="booking-calendar__panel">
                <div className="booking-calendar__panel-heading">
                  <div>
                    <p className="booking-calendar__section-label">Month view</p>
                    <h2>Select a day</h2>
                  </div>
                  <button type="button" onClick={() => {
                    const today = new Date();
                    setSelectedDate(today);
                    setActiveMonth(today);
                  }}>Today</button>
                </div>
                <Calendar
                  value={selectedDate}
                  activeStartDate={activeMonth}
                  onChange={setSelectedDate}
                  onActiveStartDateChange={({ activeStartDate }) => activeStartDate && setActiveMonth(activeStartDate)}
                  tileContent={tileContent}
                  calendarType="gregory"
                />
                <div className="booking-calendar__legend" aria-label="Booking status legend">
                  {STATUS_ORDER.map((status) => (
                    <span key={status}><i style={{ backgroundColor: STATUS_COLORS[status] }} />{statusLabel(status)}</span>
                  ))}
                </div>
              </div>

              <div className="booking-calendar__panel booking-calendar__agenda">
                <div className="booking-calendar__panel-heading">
                  <div>
                    <p className="booking-calendar__section-label">Daily agenda</p>
                    <h2>{selectedDate.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</h2>
                  </div>
                  <span className="booking-calendar__day-count">{selectedBookings.length}</span>
                </div>
                {selectedBookings.length ? (
                  <div className="booking-calendar__appointments">
                    {selectedBookings.map((booking) => <BookingCard key={booking.id} booking={booking} />)}
                  </div>
                ) : (
                  <div className="booking-calendar__empty">
                    <span>○</span>
                    <h3>No bookings this day</h3>
                    <p>Select a marked date to see its appointments.</p>
                  </div>
                )}
              </div>
            </section>

            <section className="booking-calendar__panel">
              <div className="booking-calendar__panel-heading">
                <div>
                  <p className="booking-calendar__section-label">Coming up</p>
                  <h2>Next appointments</h2>
                </div>
              </div>
              {upcomingBookings.length ? (
                <div className="booking-calendar__upcoming">
                  {upcomingBookings.map((booking) => <BookingCard key={booking.id} booking={booking} compact />)}
                </div>
              ) : <p className="booking-calendar__no-upcoming">No upcoming active bookings.</p>}
            </section>
          </>
        ) : null}
      </div>
    </main>
  );
};

export default BookingsCalendar;
